import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";

import { Bell01Icon } from "@/components/Icons/Bell01Icon";
import { NavigationSidebarUserMenu } from "@/components/NavigationSidebar/NavigationSidebarUserMenu";
import { AskNobiNavigationItem } from "@/components/PortfolioSidebar/AskNobiNavigationItem";
import { Sidebar } from "@/components/Sidebar/Sidebar";
import { SidebarItem } from "@/components/Sidebar/SidebarItem";
import type { SidebarBodyRootProps } from "@/components/Sidebar/types";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { useSessionUserOptional } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { useKey } from "@/hooks/useKey";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useConfig } from "@/providers/config/useConfig";
import { useNotificationQueries } from "@/queries/notifications/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { useProjectSidebarManager } from "./items";
import { canAccessAdminNotifications, canAccessResidentNotifications } from "./permissions";
import { ProjectSidebarHeader } from "./ProjectSidebarHeader";

interface ProjectSidebarProps extends Omit<SidebarBodyRootProps, "children"> {}
export function ProjectSidebar({
  isOpened,
  isCollapsed,
  onToggleOpen,
  onToggleCollapse,
}: ProjectSidebarProps): React.ReactNode {
  const [isProjectSwitcherVisible, projectSwitcherVisibilityHandlers] = useBool();
  const { t } = useTranslation();
  const slug = useSlug();
  const [sessionUser] = useSessionUserOptional({
    keepPreviousData: true,
  });
  const isDesktop = useIsDesktop();
  const { navigationItems, canViewPortfolio } = useProjectSidebarManager();
  const isNobiAvailable = useConfig("isNobiAvailable");
  const hasPermission = usePermission();
  const notificationQueries = useNotificationQueries();
  const isResident = sessionUser?.role.isResidentType;
  const { data: adminNotificationStats } = useQuery({
    ...notificationQueries.adminStatus({
      staleTime: 30 * 1000,
      refetchInterval: 30 * 1000,
    }),
    enabled: hasPermission(canAccessAdminNotifications),
  });
  const { data: residentNotificationStats } = useQuery({
    ...notificationQueries.residentStatus({
      staleTime: 30 * 1000,
      refetchInterval: 30 * 1000,
    }),
    enabled: hasPermission(canAccessResidentNotifications),
  });
  const canAccessNotifications =
    hasPermission(canAccessAdminNotifications) || hasPermission(canAccessResidentNotifications);
  const notificationStats = isResident
    ? {
        amount: residentNotificationStats?.unreadNotifications,
        hasUnread: !!residentNotificationStats?.unreadNotifications,
      }
    : {
        amount: adminNotificationStats?.amount,
        hasUnread: adminNotificationStats?.hasUnread,
      };
  useKey("Escape", projectSwitcherVisibilityHandlers.setFalse, isProjectSwitcherVisible);

  return (
    <Sidebar.Root
      {...{
        isOpened,
        isCollapsed,
        onToggleCollapse,
        onToggleOpen,
      }}
    >
      <AnimatePresence>
        {!sessionUser && (
          <motion.div className="absolute inset-0 z-sticky size-full bg-background-default">
            <FullSizeLoader size="sm" />
          </motion.div>
        )}
      </AnimatePresence>
      <ProjectSidebarHeader
        {...{
          onToggleOpen,
        }}
      />
      <Sidebar.Body items={navigationItems} hasSeparationLineForChildren>
        {canAccessNotifications && isDesktop && (
          <Stack gap="sm">
            {!isCollapsed && (
              <h3 className="px-space-md text-overline-emphasis tracking-wide text-text-secondary uppercase">
                {t("navigation.notifications.section-title")}
              </h3>
            )}
            <ul>
              <SidebarItem
                label={t("navigation.notifications.title")}
                newEventCount={notificationStats.amount}
                isNewEventAvailable={notificationStats.hasUnread}
                icon={<Bell01Icon />}
                to={isResident ? routes.residentNotifications.list({ slug }) : routes.adminNotifications.list({ slug })}
                data-testid={isResident ? "navigation-resident-notifications" : "navigation-admin-notifications"}
              />
            </ul>
          </Stack>
        )}
        {!isDesktop && canViewPortfolio && isNobiAvailable && <AskNobiNavigationItem />}
      </Sidebar.Body>
      <Sidebar.Footer>
        <NavigationSidebarUserMenu />
      </Sidebar.Footer>
    </Sidebar.Root>
  );
}
